import React, { useEffect, useRef, useState, KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import kvsLogo from "../assets/kvsLogo.svg";

import SQLCard       from "../modules/analytics-copilot/components/SQLCard";
import LoadingStages from "../modules/analytics-copilot/components/LoadingStages";
import ChartRenderer from "../modules/analytics-copilot/components/ChartRenderer";
import SummaryCard   from "../modules/analytics-copilot/components/SummaryCard";
import MetricCards   from "../modules/analytics-copilot/components/MetricCards";
import { useAnalyticsChat } from "../modules/analytics-copilot/context/AnalyticsChatContext";
import { AssistantChatMessage, ChatMessage, VisualizationConfig } from "../modules/analytics-copilot/types";

const AI_BASE_URL = process.env.REACT_APP_AI_API_URL ;

const FALLBACK_TABLE: VisualizationConfig = { type: "table", title: "Results", xAxis: "", series: [] };

interface CopilotDrawerProps {
  isOpen:  boolean;
  onClose: () => void;
}

// ── Bot avatar ────────────────────────────────────────────────────────────────

const BotAvatar = ({ small }: { small?: boolean }) => (
  <div
    className="rounded-full shrink-0 flex items-center justify-center bg-white shadow-sm border border-gray-100 p-0.5"
    style={{
      width:  small ? 24 : 36,
      height: small ? 24 : 36,
    }}
  >
    <img src={kvsLogo} alt="KVS Logo" className="w-full h-full object-contain" />
  </div>
);

// ── Typing dots ───────────────────────────────────────────────────────────────

const TypingDots = () => (
  <div className="flex items-center gap-1 px-3 py-2 bg-white border border-gray-100 rounded-xl rounded-tl-sm shadow-sm w-fit">
    {[0, 1, 2].map(i => (
      <span key={i} className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce"
        style={{ animationDelay: `${i * 0.15}s` }} />
    ))}
  </div>
);

// ── Assistant bubble ──────────────────────────────────────────────────────────

const AssistantBubble: React.FC<{ msg: AssistantChatMessage }> = ({ msg }) => {
  const isThinking = msg.stage === "thinking";
  const isLoading  = ["sql", "data", "summary"].includes(msg.stage);
  const hasData    = (msg.data?.length ?? 0) > 0;
  const vizConfig  = msg.visualizationConfig ?? FALLBACK_TABLE;

  return (
    <div className="flex items-start gap-2">
      <BotAvatar small />
      <div className="flex-1 min-w-0 space-y-2">
        {isThinking && <TypingDots />}
        {isLoading && (
          <div className="bg-white border border-gray-100 rounded-xl rounded-tl-sm shadow-sm px-3 py-2.5">
            <LoadingStages stage={msg.stage} />
          </div>
        )}
        {msg.stage === "error" && msg.error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
            {msg.error}
          </div>
        )}
        {msg.sqlQuery  && <SQLCard sql={msg.sqlQuery} />}
        {hasData       && <MetricCards data={msg.data!} />}
        {hasData && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3">
            <ChartRenderer key={vizConfig.type} config={vizConfig} data={msg.data!} />
          </div>
        )}
        {hasData && !msg.summary && !["error","done"].includes(msg.stage) && (
          <div className="bg-white border-l-4 border-blue-200 rounded-xl px-3 py-2.5 animate-pulse space-y-1.5">
            <div className="h-2.5 bg-gray-100 rounded w-full"/>
            <div className="h-2.5 bg-gray-100 rounded w-4/5"/>
          </div>
        )}
        {msg.summary && (
          <SummaryCard summary={msg.summary} rowCount={msg.rowCount} totalCount={msg.totalCount} />
        )}
        {msg.downloadUrl && (
          <a
            href={`${AI_BASE_URL}${msg.downloadUrl}`}
            download="kvs_analytics_export.csv"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg
              border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
            </svg>
            Download CSV
            {(msg.totalCount ?? msg.rowCount) !== null && (
              <span className="opacity-70">({(msg.totalCount ?? msg.rowCount)!.toLocaleString()}r)</span>
            )}
          </a>
        )}
      </div>
    </div>
  );
};

// ── Drawer component ──────────────────────────────────────────────────────────

const CopilotDrawer: React.FC<CopilotDrawerProps> = ({ isOpen, onClose }) => {
  const { messages, isRunning, sendQuery, reset } = useAnalyticsChat();
  const navigate   = useNavigate();
  const bottomRef  = useRef<HTMLDivElement>(null);
  const [text, setText] = useState("");
  const taRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll on new messages
  useEffect(() => {
    if (isOpen) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isOpen]);

  // Just hide the drawer — do NOT reset chat so history is preserved on re-open
  const handleClose    = () => { onClose(); };
  // Navigate to full page; shared context keeps the same messages visible there
  const handleFullPage = () => { onClose(); navigate("/analytics/copilot"); };

  const autoResize = () => {
    const ta = taRef.current;
    if (ta) { ta.style.height = "auto"; ta.style.height = Math.min(ta.scrollHeight, 120) + "px"; }
  };

  const handleSend = () => {
    const q = text.trim();
    if (!q || isRunning) return;
    sendQuery(q);
    setText("");
    if (taRef.current) taRef.current.style.height = "auto";
  };

  const handleKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleClose}
        className={`fixed inset-0 z-[55] bg-black/25 backdrop-blur-[1px] transition-opacity duration-300
          ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
      />

      {/* Slide-in panel */}
      <div
        className={`fixed top-0 right-0 bottom-0 z-[60] flex flex-col bg-gray-50 shadow-2xl
          transition-transform duration-300 ease-in-out w-[460px] max-w-[95vw]
          ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* Header */}
        <div
          className="shrink-0 flex items-center justify-between px-4 py-3"
          style={{ background: "linear-gradient(135deg,#7C3AED 0%,#4F46E5 55%,#2563EB 100%)" }}
        >
          <div className="flex items-center gap-2.5">
            <BotAvatar />
            <div>
              <p className="text-sm font-bold text-white leading-tight">Samiksha Samadhan</p>
              <p className="text-xs text-white/65">AI Analytics Copilot</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button onClick={reset}
                className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/15 transition-colors"
                title="New chat">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
                </svg>
              </button>
            )}
            <button onClick={handleFullPage}
              className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/15 transition-colors"
              title="Open full page">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
              </svg>
            </button>
            <button onClick={handleClose}
              className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/15 transition-colors"
              title="Close">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Message thread */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center space-y-3 pb-8">
              <p className="text-sm text-gray-400 leading-relaxed max-w-xs">
                Ask anything about KVS attendance, scores, or enrollment — in plain English.
              </p>
              <p className="text-xs text-gray-300">Press Enter to send · Shift+Enter for newline</p>
            </div>
          ) : (
            messages.map((msg: ChatMessage) =>
              msg.role === "user" ? (
                <div key={msg.id} className="flex justify-end">
                  <div
                    className="max-w-[80%] px-3.5 py-2.5 rounded-2xl rounded-tr-sm text-sm text-white leading-relaxed"
                    style={{ background: "linear-gradient(135deg,#7C3AED 0%,#2563EB 100%)" }}
                  >
                    {msg.query}
                  </div>
                </div>
              ) : (
                <AssistantBubble key={msg.id} msg={msg as AssistantChatMessage} />
              )
            )
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input bar */}
        <div className="shrink-0 border-t border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex items-end gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2.5
            shadow-sm focus-within:border-indigo-400 transition-colors">
            <textarea
              ref={taRef}
              value={text}
              rows={1}
              onChange={e => { setText(e.target.value); autoResize(); }}
              onKeyDown={handleKey}
              disabled={isRunning}
              placeholder="Ask a question…"
              className="flex-1 resize-none bg-transparent text-sm text-gray-800
                placeholder-gray-400 outline-none leading-relaxed disabled:opacity-60"
              style={{ maxHeight: 120 }}
            />
            <button
              onClick={handleSend}
              disabled={isRunning || !text.trim()}
              className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center
                transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                background: isRunning || !text.trim()
                  ? "#E5E7EB"
                  : "linear-gradient(135deg,#7C3AED 0%,#2563EB 100%)",
              }}
            >
              {isRunning ? (
                <svg className="w-3.5 h-3.5 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CopilotDrawer;
